import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatCents, formatDate } from '../lib/format.js';
import { useAuth } from '../lib/auth-context.js';

const { t } = i18n;

/** Internal staff — the only roles allowed to see storage paid-until aging. */
const INTERNAL_ROLES = ['ADMIN', 'OPERATOR', 'ACCOUNTANT'];

interface RevenueRow {
  id: string;
  code: string;
  name: string;
  totalCents: number;
}
interface StorageRevenue {
  periodFrom: string;
  periodTo: string;
  totalCents: number;
  customers: RevenueRow[];
  partners: RevenueRow[];
}

interface AgingRow {
  kind: 'customer' | 'partner';
  code: string;
  name: string;
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  paidUntil: string | null;
  lastPaymentAt: string | null;
  bucket: 'green' | 'amber' | 'red' | 'unknown';
}

interface PartnerOpt {
  id: string;
  code: string;
  name: string;
}

const BUCKET_CLASS: Record<AgingRow['bucket'], string> = {
  green: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100',
  amber: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100',
  red: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100',
  unknown: 'bg-muted text-muted-foreground border-border',
};

export function ReportsPage() {
  const { user } = useAuth();
  const isInternal = user?.roles.some((r) => INTERNAL_ROLES.includes(r.role)) ?? false;
  const [aging, setAging] = useState<AgingRow[] | null>(null);
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [revenue, setRevenue] = useState<StorageRevenue | null>(null);
  const [revFrom, setRevFrom] = useState('');
  const [revTo, setRevTo] = useState('');

  useEffect(() => {
    // Paid-until aging is storage-side data restricted to internal staff;
    // don't even request it for partner-tier users (they'd get a 403).
    if (isInternal) {
      api<AgingRow[]>('/reports/paid-until-aging').then(setAging).catch(() => setAging([]));
    }
    api<PartnerOpt[]>('/partners').then(setPartners).catch(() => setPartners([]));
  }, [isInternal]);

  useEffect(() => {
    if (!isInternal) return;
    const qs = new URLSearchParams();
    if (revFrom) qs.set('from', revFrom);
    if (revTo) qs.set('to', revTo);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    setRevenue(null);
    api<StorageRevenue>(`/billing/storage-revenue${suffix}`)
      .then(setRevenue)
      .catch(() => setRevenue(null));
  }, [isInternal, revFrom, revTo]);

  return (
    <section className="space-y-6">
      <h2 className="text-xl font-semibold">{t('reports.title')}</h2>

      {isInternal ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('reports.storage_revenue')}
            </h3>
            <div className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-1">
                <span className="text-muted-foreground">{t('common.from')}</span>
                <input
                  type="date"
                  value={revFrom}
                  onChange={(e) => setRevFrom(e.target.value)}
                  className="rounded border border-border bg-background px-2 py-1"
                />
              </label>
              <label className="flex items-center gap-1">
                <span className="text-muted-foreground">{t('common.to')}</span>
                <input
                  type="date"
                  value={revTo}
                  onChange={(e) => setRevTo(e.target.value)}
                  className="rounded border border-border bg-background px-2 py-1"
                />
              </label>
            </div>
          </div>
          {revenue === null ? (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : (
            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">{t('reports.revenue_total')}</span>
                <span className="text-xl font-semibold tabular-nums">
                  {formatCents(revenue.totalCents)}
                </span>
              </div>
              {[...revenue.customers, ...revenue.partners].length === 0 ? (
                <div className="text-center text-sm text-muted-foreground">
                  {t('reports.revenue_empty')}
                </div>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {[...revenue.customers, ...revenue.partners].map((r) => (
                    <li key={r.id} className="flex items-center justify-between py-1.5">
                      <span>
                        <span className="font-mono text-xs text-muted-foreground mr-2">{r.code}</span>
                        {r.name}
                      </span>
                      <span className="tabular-nums">{formatCents(r.totalCents)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      ) : null}

      {isInternal ? (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('reports.paid_until_aging')}
          </h3>
          <a
            href="/api/reports/paid-until-aging.csv"
            download
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('reports.download_csv')}
          </a>
        </div>
        {aging === null ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : aging.length === 0 ? (
          <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            {t('reports.empty')}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('customer.col.code')}</th>
                    <th className="px-3 py-2 font-medium">{t('customer.col.name')}</th>
                    <th className="px-3 py-2 font-medium">{t('customer.col.paid_until')}</th>
                    <th className="px-3 py-2 font-medium">{t('customer.col.last_payment')}</th>
                    <th className="px-3 py-2 font-medium">{t('reports.bucket')}</th>
                  </tr>
                </thead>
                <tbody>
                  {aging.map((r) => (
                    <tr key={`${r.kind}-${r.code}`} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-3 py-2">
                        {r.name}
                        {r.kind === 'partner' ? (
                          <span className="ml-2 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                            {t('nav.partners')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{formatDate(r.paidUntil)}</td>
                      <td className="px-3 py-2">{formatDate(r.lastPaymentAt)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${BUCKET_CLASS[r.bucket]}`}
                        >
                          {t(`customer.paid_until.tone.${r.bucket}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="md:hidden space-y-2">
              {aging.map((r) => (
                <li key={`${r.kind}-${r.code}`} className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-sm">
                        {r.name}
                        {r.kind === 'partner' ? (
                          <span className="ml-2 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                            {t('nav.partners')}
                          </span>
                        ) : null}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">{r.code}</div>
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${BUCKET_CLASS[r.bucket]}`}
                    >
                      {t(`customer.paid_until.tone.${r.bucket}`)}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    <dt className="text-muted-foreground">{t('customer.col.paid_until')}</dt>
                    <dd className="text-right">{formatDate(r.paidUntil)}</dd>
                    <dt className="text-muted-foreground">{t('customer.col.last_payment')}</dt>
                    <dd className="text-right">{formatDate(r.lastPaymentAt)}</dd>
                  </dl>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      ) : null}

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('reports.stock_by_partner')}
        </h3>
        {partners.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t('reports.no_partners')}</div>
        ) : (
          <ul className="space-y-1">
            {partners.map((p) => (
              <li key={p.id} className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm">
                <span>
                  <span className="font-mono text-xs text-muted-foreground mr-2">{p.code}</span>
                  {p.name}
                </span>
                <a
                  href={`/api/reports/stock.csv?partnerId=${p.id}`}
                  download
                  className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                >
                  {t('reports.download_csv')}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
